<script setup lang="ts">
import { onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSeoMeta } from '@unhead/vue'

import { IMPACT_TYPES, type IProjectRequestParams } from '@/api/interfaces'
import { useEditorStore } from '@/stores/Editor'
import { usePublicData } from '@/stores/PublicData'
import { useEditorFrame, EVENT_TYPES } from '@/composables/iframe'
import { inIFrame } from '@/helpers/utils'

import PublicComparisonCardRow from '@/components/public-page/PublicComparisonCardRow.vue'
import PublicComparisonCardLoader from '@/components/public-page/PublicComparisonCardLoader.vue'
import PublicHeader from '@/components/public-page/PublicHeader.vue'
import PublicHero from '@/components/public-page/PublicHero.vue'
import PublicMap from '@/components/public-page/PublicMap.vue'
import PublicTopStatCardList from '@/components/public-page/PublicTopStatCardList.vue'
import PublicLedger from '@/components/public-page/PublicLedger.vue'
import PublicFooter from '@/components/public-page/PublicFooter.vue'
import PublicCTA from '@/components/public-page/PublicCTA.vue'
import PublicMapLoader from '@/components/public-page/PublicMapLoader.vue'
import PublicGoalsAchievements from '@/components/public-page/PublicGoalsAchievements.vue'

const publicDataStore = usePublicData()
const editorStore = useEditorStore()
const route = useRoute()
const { dispatchMessage } = useEditorFrame()
const companyId: string = route.params.id as string
const projectParams: IProjectRequestParams = {
  statuses: ['approved', 'deprecated'],
}
const pageTitle = `${route.params.name}'s sustainable impact with Greenspark`
const pageDescription = `${route.params.name}'s partnered with Greenspark to achieve their sustainable and environmental goals such as planting trees, rescuing plastic, offsetting CO2. Check out their journey so far!`

const init = () => {
  publicDataStore.loading = true

  Promise.all([
    publicDataStore.fetchPublicData(companyId),
    editorStore.fetchEditorSettings(companyId),
    publicDataStore.fetchProjects({ ...projectParams }),
    publicDataStore.fetchPurchases(),
  ])
    .catch((error) => console.error(error))
    .finally(() => {
      publicDataStore.loading = false

      if (inIFrame()) {
        dispatchMessage(window.parent, { type: EVENT_TYPES.INIT })
      }
    })
}

onMounted(() => {
  init()
})

useSeoMeta({
  title: pageTitle,
  description: pageDescription,
  ogTitle: pageTitle,
  ogType: 'website',
  ogDescription: pageDescription,
  ogUrl: window.location.href,
  ogImage:
    'https://cdn.prod.website-files.com/611391a1477389e3857d8014/673c84ac1ebcf01d18a6d07d_OG%20Image.png',
})
</script>

<template>
  <div
    :class="[
      'public-page',
      editorStore.editor.background.theme,
      editorStore.editor.background.icon,
    ]"
  >
    <PublicHeader />
    <PublicHero
      :editor-settings="editorStore.editor.hero"
      :theme="editorStore.editor.background.theme"
    />
    <PublicTopStatCardList
      v-if="publicDataStore.getTopStats"
      :icon-style="editorStore.editor.icon.style"
      :color-settings="editorStore.editor.icon.impactColors"
      :public-stat-data="publicDataStore.getPublicStats"
      :top-stat-data="publicDataStore.getTopStats"
    />
    <PublicMap
      v-if="publicDataStore.projectList.length && !publicDataStore.loading"
      :projects="publicDataStore.getActiveAndDeprecatedProjects"
      :color-settings="editorStore.editor.icon.impactColors"
    />
    <PublicMapLoader v-if="publicDataStore.loading" />
    <template v-for="impactType in IMPACT_TYPES" :key="impactType">
      <PublicComparisonCardRow
        v-if="
          publicDataStore.getTopStats[impactType] &&
          editorStore.editor.icon.impactDisplay[impactType] &&
          !publicDataStore.loading
        "
        :icon-style="editorStore.editor.icon.style"
        :color-settings="editorStore.editor.icon.impactColors"
        :impact-type="impactType"
        :top-stat-data="publicDataStore.getTopStats"
      />
      <PublicComparisonCardLoader v-if="publicDataStore.loading" />
    </template>
    <PublicGoalsAchievements
      v-if="
        editorStore.editor.goalsAchievements[publicDataStore.lang]?.goals.length > 0 ||
        editorStore.editor.goalsAchievements[publicDataStore.lang]?.achievements.length > 0
      "
    />
    <PublicLedger
      :purchases="publicDataStore.purchases"
      :transferred-receipts="publicDataStore.getTransferredImpactInvoices"
    />
    <PublicCTA />
    <PublicFooter />
  </div>
</template>

<style scoped lang="scss">
.public-page {
  background-color: var(--gray-light-f1);
  color: var(--ui-black);

  &.dark {
    background-color: var(--ui-dark);
    color: var(--lightest);
  }

  &.custom {
    background-color: rgba(var(--custom-theme-color-rgb), 0.5);
    color: var(--lightest);
  }
}
</style>
